import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { formatInteger } from 'utils/formatting'
import { Tabs } from 'components/Tabs'
import useApiService from 'services/api'
import { MobileListViewHeader } from 'components/MobileListViewHeader'
import { EmailMessage } from 'models/emailIntegration'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import {
  PageContainer,
  BackButton,
} from 'components/MobileSearch/MobileSearch.styles'
import { ChevronLeft } from 'lucide-react'
import { dataTableSortingStoreRepo } from 'components/DataTable/DataTableSorting/DataTableSortingStore'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { getTableQueryKey } from '../../../utils/getTableQueryKey'
import { MobileViewEmailModal } from './MobileViewEmailModal'
import styled from 'styled-components'

const EMAILS_TABLE_KEY = 'emails_table_mobile' // define wherever CONTACTS_TABLE_KEY lives

const PAGE_SIZE = 20

const sortingOptions = [
  { label: 'Date', value: 'date' },
  { label: 'Date', value: '-date', desc: true },
  { label: 'Subject', value: 'subject' },
  { label: 'Subject', value: '-subject', desc: true },
]

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const TwoColumnLayout = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  @media (min-width: 768px) {
    flex-direction: row;
  }
`

const ListColumn = styled.div`
  flex: 1;
  min-width: 0;
  border-right: 1px solid #e5e7eb;
  @media (max-width: 767px) {
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
  }
`

const DetailColumn = styled.div`
  flex: 2;
  min-width: 0;
  display: none;
  @media (min-width: 768px) {
    display: block;
  }
`

const EmailItem = styled.div<{ selected?: boolean }>`
  border-bottom: 1px solid #e5e7eb;
  cursor: pointer;
  padding: 12px;
  transition: all 0.2s;
  &:hover {
    background: #f8fafc;
  }
`

const EmailRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`

const EmailIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #cecece;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 14px;
  flex-shrink: 0;
`

const EmailContent = styled.div`
  flex: 1;
  min-width: 0;
`

const Subject = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: #111827;
  margin-bottom: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const Snippet = styled.div`
  font-size: 14px;
  color: #6b7280;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-bottom: 8px;
`

const Meta = styled.div`
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #9ca3af;
`

const NoEmails = styled.div`
  text-align: center;
  padding: 40px 16px;
  color: #6b7280;
`

export function MobileEmailsPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [selectedEmailIndex, setSelectedEmailIndex] = useState<number | null>(
    null
  )
  const [selectedTab, setSelectedTab] = useState<'received' | 'sent'>(
    'received'
  )

  const api = useApiService()
  const IDENTIFIER = 'EMAILS_TABLE'

  const filterIdFromUrl = searchParams.get('contact_company_id')

  const sortingStore = dataTableSortingStoreRepo.getStore(EMAILS_TABLE_KEY)()
  const { sorting } = sortingStore

  const contactsFilterStore = filterStoreRepo.getStore(IDENTIFIER)
  const filterStore = contactsFilterStore()
  const { page, setPage } = filterStore

  const filtersAsParams = useFilterParams(contactsFilterStore)

  const filterParams = useMemo(
    () => ({ ...filtersAsParams }),
    [filtersAsParams]
  )

  const sortParams = useMemo(() => {
    if (!sorting?.length) return {}
    return { sort: sorting[0].desc ? `-${sorting[0]?.id}` : sorting[0]?.id }
  }, [sorting])

  useEffect(() => {
    if (page > 1) {
      setPage(1)
    }
  }, [filterIdFromUrl, setPage, selectedTab])

  const filterAndSortParams = useMemo(
    () => ({
      ...filterParams,
      ...sortParams,
      page,
      page_size: PAGE_SIZE,
      ...(filterIdFromUrl ? { chain_proxy_id: filterIdFromUrl } : {}),
      outbound: selectedTab === 'sent',
    }),
    [filterParams, sortParams, page, filterIdFromUrl, selectedTab]
  )

  // The active tab's total comes from the list query's `count` (below), so we
  // only need a dedicated count query for the *inactive* tab — two requests
  // instead of three.
  const inactiveOutbound = selectedTab !== 'sent'

  // Mirror the active tab's filters so both badges count the same filtered set
  // (sort is intentionally omitted — it never changes a count). Filters go in
  // the queryKey too, or the count wouldn't refetch when the filter changes.
  const inactiveCountParams = useMemo(
    () => ({
      ...filterParams,
      ...(filterIdFromUrl ? { chain_proxy_id: filterIdFromUrl } : {}),
      outbound: inactiveOutbound,
      page_size: 1,
    }),
    [filterParams, filterIdFromUrl, inactiveOutbound]
  )

  const { data: inactiveCountData } = useQuery({
    queryKey: ['emails-count', filterIdFromUrl, inactiveOutbound, filterParams],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      api.emailIntegration.getEmailMessageList(inactiveCountParams, signal),
    placeholderData: keepPreviousData,
  })

  const queryKey = getTableQueryKey({
    tableKey: EMAILS_TABLE_KEY,
    filterParams: filterAndSortParams,
    page,
  })

  const { data, isFetching } = useQuery({
    queryKey,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      api.emailIntegration.getEmailMessageList(filterAndSortParams, signal),
    // Keep the previous tab's list (and its `count`) visible while the new tab
    // loads, so the active badge doesn't flash to 0 on every tab switch.
    placeholderData: keepPreviousData,
  })

  // Active tab's count comes from the list response; the inactive tab's from
  // its own lightweight (page_size=1) query above.
  const listCount = data?.count ?? 0
  const inactiveCount = inactiveCountData?.count ?? 0
  const receivedCount = selectedTab === 'received' ? listCount : inactiveCount
  const sentCount = selectedTab === 'sent' ? listCount : inactiveCount

  const handleViewEmail = (index: number) => {
    setSelectedEmailIndex(index)
  }

  const emails = data?.results || []

  return (
    <PageContainer>
      <BackButton onClick={() => navigate(-1)}>
        <ChevronLeft size={20} />
        <span>Back</span>
      </BackButton>

      <div className="w-full mt-3 ml-2">
        <Tabs
          tabs={[
            {
              key: 'received',
              title: `Received (${formatInteger(receivedCount)})`,
            },
            {
              key: 'sent',
              title: `Sent (${formatInteger(sentCount)})`,
            },
          ]}
          selectedTab={selectedTab}
          setSelectedTab={(tab) => setSelectedTab(tab as 'received' | 'sent')}
        />
      </div>

      <MobileListViewHeader
        sortingOptions={sortingOptions}
        filterStoreIdentifier={IDENTIFIER}
        tableKey={EMAILS_TABLE_KEY}
        defaultSorting={[{ id: 'date', desc: true }]}
      />

      <FilterChips
        store={contactsFilterStore}
        identifier={IDENTIFIER}
        clearAllButton
        showActive
      />

      <Content>
        <TwoColumnLayout>
          <ListColumn>
            {isFetching ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-lg text-gray-500">Loading...</div>
              </div>
            ) : emails.length ? (
              emails.map((email: EmailMessage, index: number) => (
                <EmailItem
                  key={email.id}
                  onClick={() => handleViewEmail(index)}
                  selected={selectedEmailIndex === index}
                >
                  <EmailRow>
                    <EmailIcon>{email.outbound ? 'OUT' : 'IN'}</EmailIcon>
                    <EmailContent>
                      <Subject>{email.subject || '(no subject)'}</Subject>
                      <Snippet>{email.snippet}</Snippet>
                      <Meta>
                        <span>
                          {email.related_contacts?.[0]?.first_name}{' '}
                          {email.related_contacts?.[0]?.last_name ||
                            'No contact'}
                        </span>
                        <span>{new Date(email.date).toLocaleDateString()}</span>
                      </Meta>
                    </EmailContent>
                  </EmailRow>
                </EmailItem>
              ))
            ) : (
              <NoEmails>No emails found</NoEmails>
            )}
          </ListColumn>

          <DetailColumn>
            {/* Desktop detail view - not used on mobile */}
          </DetailColumn>
        </TwoColumnLayout>
      </Content>

      {selectedEmailIndex !== null && (
        <MobileViewEmailModal
          isOpen={selectedEmailIndex !== null}
          onClose={() => setSelectedEmailIndex(null)}
          emails={emails}
          initialIndex={selectedEmailIndex}
          emailType={selectedTab}
        />
      )}
    </PageContainer>
  )
}
